// src/pages/Contact/ContactUs.jsx
import React from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import QuerySection from '../../components/common/QuerySection/QuerySection';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';

const ContactUs = () => {
    // WinNest office coordinates
    const officeLocation = {
        lat: 12.9352,
        lng: 77.6245,
        address: "#436, 2nd Floor, 9th Cross, Next to Sonata Southend, R.V. Road, Jayanagar 2nd Block, Bengaluru, 560011"
    };

    const openInGoogleMaps = () => {
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(officeLocation.address)}`;
        window.open(googleMapsUrl, '_blank');
    };

    const openDirections = () => {
        const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(officeLocation.address)}`;
        window.open(directionsUrl, '_blank');
    };

    return (
        <>
            <style>{`
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }
                
                .contact-form {
                    transition: all 0.3s ease;
                }
                
                .form-input {
                    transition: all 0.3s ease;
                    border: 2px solid #e5e7eb;
                }
                
                .form-input:focus {
                    outline: none;
                    border-color: #22c55e;
                    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
                }
                
                .contact-card {
                    transition: all 0.3s ease;
                }
                
                .contact-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                }
                
                .submit-button {
                    transition: all 0.3s ease;
                }
                
                .submit-button:hover {
                    background-color: #16a34a;
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(34, 197, 94, 0.3);
                }

                .map-container {
                    transition: all 0.3s ease;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .map-container:hover {
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                }

                .map-overlay {
                    position: absolute;
                    bottom: 20px;
                    left: 20px;
                    right: 20px;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    border-radius: 8px;
                    padding: 1rem;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .map-button {
                    transition: all 0.3s ease;
                    border: none;
                    cursor: pointer;
                    border-radius: 6px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .map-button:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .map-button.primary {
                    background-color: #22c55e;
                    color: white;
                    padding: 0.75rem 1.5rem;
                }

                .map-button.primary:hover {
                    background-color: #16a34a;
                }

                .map-button.secondary {
                    background-color: #f3f4f6;
                    color: #374151;
                    padding: 0.75rem 1.5rem;
                    margin-left: 0.5rem;
                }

                .map-button.secondary:hover {
                    background-color: #e5e7eb;
                }
                
                @media (max-width: 1024px) {
                    .hero-container {
                        flex-direction: column;
                        text-align: center;
                        gap: 3rem;
                        min-height: auto;
                        padding: 2rem;
                    }
                    
                    .contact-content {
                        grid-template-columns: 1fr !important;
                        gap: 3rem !important;
                    }
                    
                    .contact-info-grid {
                        grid-template-columns: 1fr !important;
                    }

                    .map-and-info {
                        flex-direction: column !important;
                    }

                    .map-overlay {
                        position: relative !important;
                        bottom: auto !important;
                        left: auto !important;
                        right: auto !important;
                        margin-top: 1rem;
                    }
                }
                
                @media (max-width: 768px) {
                    .hero-section {
                        padding: 4rem 0 !important;
                        min-height: 400px !important;
                    }
                    
                    .hero-title {
                        font-size: 2.5rem !important;
                    }
                    
                    .hero-container p {
                        font-size: 1.1rem !important;
                    }
                    
                    .contact-section {
                        padding: 4rem 0 !important;
                    }
                    
                    .info-section {
                        padding: 4rem 0 !important;
                    }

                    .map-button {
                        padding: 0.6rem 1.2rem !important;
                        font-size: 0.9rem;
                    }

                    .map-overlay {
                        padding: 0.75rem !important;
                    }
                }
                
                @media (max-width: 640px) {
                    .hero-container, .contact-container, .info-container {
                        padding: 0 1rem !important;
                    }
                    
                    .hero-title {
                        font-size: 2rem !important;
                    }
                    
                    .hero-container p {
                        font-size: 1rem !important;
                    }
                    
                    .contact-form {
                        padding: 1.5rem !important;
                    }

                    .map-button {
                        padding: 0.5rem 1rem !important;
                        font-size: 0.85rem;
                    }

                    .map-button.secondary {
                        margin-left: 0 !important;
                        margin-top: 0.5rem;
                        width: 100%;
                        justify-content: center;
                    }

                    .map-overlay {
                        left: 10px !important;
                        right: 10px !important;
                        bottom: 10px !important;
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
                {/* Header */}
                <Header />

                {/* Hero Section */}
                <section style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)', // Financial charts and graphs
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    padding: '6rem 0',
                    position: 'relative',
                    minHeight: '500px'
                }}>
                    {/* Dark Gradient overlay for text visibility */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.5) 100%)',
                        borderRadius: '0'
                    }} />
                    
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4rem',
                        minHeight: '400px',
                        position: 'relative',
                        zIndex: 1
                    }} className="hero-container">
                        <div style={{
                             textAlign: 'center',
                            maxWidth: '800px'
                        }}>
                            <h1 style={{
                                fontSize: '4rem',
                                fontWeight: '700',
                                color: '#d9d9d9ee',
                                margin: '0 0 1rem 0',
                                lineHeight: '1.2',
                                textShadow: '0 2px 4px rgba(224, 229, 230, 0.1)'
                            }} className="hero-title">
                                Contact Us
                            </h1>
                            <p style={{
                                fontSize: '1.3rem',
                                color: '#edf1f7ff',
                                margin: '0',
                                lineHeight: '1.6',
                                maxWidth: '600px',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}>
                                We work together towards your financial planning goals. Get in touch with our team for personalized investment guidance.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Contact Information Section */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: '6rem 0'
                }} className="info-section">
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 2rem'
                    }} className="info-container">
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: '4rem',
                            marginBottom: '4rem'
                        }}>
                            <div style={{ flex: 1, maxWidth: '400px' }}>
                                <h2 style={{
                                    fontSize: '2.5rem',
                                    fontWeight: '700',
                                    color: '#1a1a1a',
                                    margin: '0',
                                    lineHeight: '1.2'
                                }}>
                                    We all can work together with WinNest.
                                </h2>
                            </div>
                            <div style={{ flex: 2 }}>
                                <p style={{
                                    fontSize: '1.1rem',
                                    color: '#4b5563',
                                    lineHeight: '1.6',
                                    marginBottom: '1.5rem'
                                }}>
                                    Financial planning requires a disciplined and steady approach for long-term wealth creation. WinNest provides professional guidance for your investment needs. <span style={{ fontSize: '0.9rem', color: '#6b7280', fontStyle: 'italic' }}>Mutual fund investments are subject to market risks. Please read all scheme related documents carefully before investing.</span>
                                </p>
                            </div>
                        </div>
                        
                        {/* Contact Cards */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '2rem'
                        }} className="contact-info-grid">
                            {/* Office Address */}
                            <div style={{
                                backgroundColor: '#f9fafb',
                                padding: '2rem',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '1px solid #e5e7eb'
                            }} className="contact-card">
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    backgroundColor: '#22c55e',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem'
                                }}>
                                    <MapPin size={28} color="white" />
                                </div>
                                <h3 style={{
                                    fontSize: '1.3rem',
                                    fontWeight: '600',
                                    color: '#1a1a1a',
                                    marginBottom: '1rem'
                                }}>
                                    Office Address
                                </h3>
                                <p style={{
                                    fontSize: '1rem',
                                    color: '#6b7280',
                                    lineHeight: '1.6'
                                }}>
                                    #436, 2nd Floor, 9th Cross, Next to Sonata Southend,<br/>
                                    R.V. Road, Jayanagar 2nd Block,<br/>
                                    Bengaluru, 560011
                                </p>
                            </div>

                            {/* Phone */}
                            <div style={{
                                backgroundColor: '#f9fafb',
                                padding: '2rem',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '1px solid #e5e7eb'
                            }} className="contact-card">
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    backgroundColor: '#22c55e',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem'
                                }}>
                                    <Phone size={28} color="white" />
                                </div>
                                <h3 style={{
                                    fontSize: '1.3rem',
                                    fontWeight: '600',
                                    color: '#1a1a1a',
                                    marginBottom: '1rem'
                                }}>
                                    Phone Numbers
                                </h3>
                                <p style={{
                                    fontSize: '1rem',
                                    color: '#6b7280',
                                    lineHeight: '1.6'
                                }}>
                                    +91 90360 24568<br/>
                                    +91 90360 25568
                                </p>
                            </div>

                            {/* Email */}
                            <div style={{
                                backgroundColor: '#f9fafb',
                                padding: '2rem',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '1px solid #e5e7eb'
                            }} className="contact-card">
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    backgroundColor: '#22c55e',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem'
                                }}>
                                    <Mail size={28} color="white" />
                                </div>
                                <h3 style={{
                                    fontSize: '1.3rem',
                                    fontWeight: '600',
                                    color: '#1a1a1a',
                                    marginBottom: '1rem'
                                }}>
                                    Email Address
                                </h3>
                                <p style={{
                                    fontSize: '1rem',
                                    color: '#6b7280',
                                    lineHeight: '1.6'
                                }}>
                                    vcare@wealthnest.in
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Map Section */}
                <section style={{
                    backgroundColor: '#f8f9fa',
                    padding: '6rem 0'
                }} className="map-section">
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 2rem'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '3rem'
                        }}>
                            <h2 style={{
                                fontSize: '2.5rem',
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '1rem'
                            }}>
                                Find Us Here
                            </h2>
                            <p style={{
                                fontSize: '1.1rem',
                                color: '#6b7280',
                                maxWidth: '600px',
                                margin: '0 auto',
                                lineHeight: '1.6'
                            }}>
                                Visit our office in Jayanagar, Bengaluru for mutual fund distribution services. <span style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>Mutual fund distribution services are provided as per AMFI regulations.</span>
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '3rem',
                            alignItems: 'stretch',
                            marginBottom: '3rem'
                        }} className="map-and-info">
                            {/* Location Details */}
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1.5rem'
                            }}>
                                <div style={{
                                    backgroundColor: '#ffffff',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb'
                                }} className="contact-card">
                                    <h3 style={{
                                        fontSize: '1.2rem',
                                        fontWeight: '600',
                                        color: '#1a1a1a',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <MapPin size={20} color="#22c55e" style={{ marginRight: '0.5rem' }} />
                                        Address
                                    </h3>
                                    <p style={{
                                        fontSize: '0.95rem',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        #436, 2nd Floor, 9th Cross<br/>
                                        Next to Sonata Southend<br/>
                                        R.V. Road, Jayanagar 2nd Block<br/>
                                        Bengaluru, Karnataka 560011
                                    </p>
                                </div>
                            </div>

                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1.5rem'
                            }}>
                                <div style={{
                                    backgroundColor: '#ffffff',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb'
                                }} className="contact-card">
                                    <h3 style={{
                                        fontSize: '1.2rem',
                                        fontWeight: '600',
                                        color: '#1a1a1a',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <Clock size={20} color="#22c55e" style={{ marginRight: '0.5rem' }} />
                                        Office Hours
                                    </h3>
                                    <div style={{
                                        fontSize: '0.95rem',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        <div style={{ marginBottom: '0.5rem' }}>
                                            <strong>Monday - Saturday:</strong> 9:00 AM - 6:00 PM
                                        </div>
                                        <div>
                                            <strong>Sunday:</strong> Closed
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Interactive Map - Full Width */}
                        <div style={{
                            width: '100%',
                            position: 'relative',
                            minHeight: '400px'
                        }}>
                            <div className="map-container" style={{
                                width: '100%',
                                height: '500px',
                                position: 'relative'
                            }}>
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.5053209742746!2d77.57817707573115!3d12.939484187372996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bbd00fc205a40d1%3A0xe45056c74ec6bf1f!2sWinNest%20Private%20Limited!5e0!3m2!1sen!2sin!4v1784264024746!5m2!1sen!2sin"
                                    width="100%"
                                    height="100%"
                                    style={{
                                        border: 0,
                                        borderRadius: '12px'
                                    }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="WinNest Office Location"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why Choose WinNest Section */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: '6rem 0'
                }} className="contact-section">
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 2rem'
                    }} className="contact-container">
                        <div style={{
                            maxWidth: '800px',
                            margin: '0 auto',
                            textAlign: 'center'
                        }}>
                            <h3 style={{
                                fontSize: '2rem',
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '2rem'
                            }}>
                                Why Choose WinNest?
                            </h3>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '2rem',
                                marginBottom: '3rem'
                            }}>
                                <div style={{
                                    backgroundColor: '#f9fafb',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '1px solid #e5e7eb'
                                }} className="contact-card">
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#22c55e',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1.5rem',
                                        fontSize: '1.5rem',
                                        fontWeight: '600',
                                        color: 'white'
                                    }}>
                                        1
                                    </div>
                                    <h4 style={{
                                        fontSize: '1.2rem',
                                        fontWeight: '600',
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }}>
                                        Personalized Guidance
                                    </h4>
                                    <p style={{
                                        fontSize: '1rem',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        Our mutual fund distribution team provides guidance tailored to your financial objectives. <span style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>Mutual fund investments are subject to market risks.</span>
                                    </p>
                                </div>

                                <div style={{
                                    backgroundColor: '#f9fafb',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '1px solid #e5e7eb'
                                }} className="contact-card">
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#22c55e',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1.5rem',
                                        fontSize: '1.5rem',
                                        fontWeight: '600',
                                        color: 'white'
                                    }}>
                                        2
                                    </div>
                                    <h4 style={{
                                        fontSize: '1.2rem',
                                        fontWeight: '600',
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }}>
                                        Comprehensive Planning
                                    </h4>
                                    <p style={{
                                        fontSize: '1rem',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        We offer investment planning services across various financial instruments. <span style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>Please read all related documents before investing.</span>
                                    </p>
                                </div>

                                <div style={{
                                    backgroundColor: '#f9fafb',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '1px solid #e5e7eb'
                                }} className="contact-card">
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#22c55e',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1.5rem',
                                        fontSize: '1.5rem',
                                        fontWeight: '600',
                                        color: 'white'
                                    }}>
                                        3
                                    </div>
                                    <h4 style={{
                                        fontSize: '1.2rem',
                                        fontWeight: '600',
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }}>
                                        Transparent Approach
                                    </h4>
                                    <p style={{
                                        fontSize: '1rem',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        We maintain transparency in our distribution services and scheme recommendations. <span style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>All services are provided as per AMFI regulations.</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <QuerySection />

                {/* Layout */}
                <Layout />

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default ContactUs;